import React, { memo } from 'react';

import PropTypes from 'prop-types';

import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import { isIdentityMasterFieldKey } from '../../../../store/products/helpers/buildIdentityMasterFieldUpdate';
import { aggregatedStringFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { isAggregatedIdentityFieldEditable } from '../helpers/aggregatedFieldHelpers';
import { mapAggregatedFieldToInputProps } from '../helpers/mapAggregatedFieldToInputProps';
import { resolveAggregatedFieldTooltip } from '../helpers/resolveAggregatedFieldTooltip';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import AggregatedScalarField from './AggregatedScalarField';

const AggregatedBoundScalarField = ({
  fieldKey,
  field,
  label,
  readOnly,
  readOnlyTooltip,
  multiline,
  mapField,
  nested,
  isMeta,
  mapCommitValue,
}) => {
  const resolvedField = mapField ? mapField(field) : field;
  const {
    value: fieldValue,
    placeholder,
    readOnly: inputReadOnly,
    isMultiple,
  } = mapAggregatedFieldToInputProps(resolvedField);
  const bindings = useBindAggregatedField(fieldKey, field, label, {
    isMeta,
    dialogLabel: typeof label === 'string' ? label : undefined,
  });
  const isIdentity = isIdentityMasterFieldKey(fieldKey, { isMeta });
  const identityLocked = isIdentity && !isAggregatedIdentityFieldEditable(resolvedField);
  const isStructuredValue = resolvedField?.value != null && typeof resolvedField.value === 'object';
  const isMultipleState = resolvedField?.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const canSet = !readOnly && !identityLocked && !inputReadOnly && !isStructuredValue;
  const canClear = !readOnly && !identityLocked && (
    (!isStructuredValue && Boolean(fieldValue || isMultiple))
    || (isStructuredValue && isMultipleState)
  );
  const fieldReadOnly = !canSet;

  const onChange = canSet
    ? (value, revert) => {
      bindings.onChange(mapCommitValue ? mapCommitValue(value) : value, revert);
    }
    : undefined;

  return (
    <AggregatedScalarField
      label={label}
      value={fieldValue}
      placeholder={placeholder}
      readOnly={fieldReadOnly}
      tooltip={resolveAggregatedFieldTooltip({
        field: resolvedField,
        layerReadOnly: readOnly,
        layerReadOnlyTooltip: readOnlyTooltip,
        isIdentity,
        canSet,
        canClear,
      })}
      multiline={multiline}
      nested={nested}
      showClearButton={canClear}
      onChange={onChange}
      onClear={canClear ? bindings.onClear : undefined}
    />
  );
};

AggregatedBoundScalarField.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  field: aggregatedStringFieldPropType,
  label: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  multiline: PropTypes.bool,
  mapField: PropTypes.func,
  nested: PropTypes.bool,
  isMeta: PropTypes.bool,
  mapCommitValue: PropTypes.func,
};

AggregatedBoundScalarField.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
  multiline: false,
  mapField: null,
  nested: false,
  isMeta: false,
  mapCommitValue: null,
};

export default memo(AggregatedBoundScalarField);
